import React from 'react';
import { Target, Heart, Award, TrendingUp, Users, Clock } from 'lucide-react';
import { Card } from '../components/ui/card';

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-red-600 text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2V8ZW58MHx8fHwxNzY0MzYzNjc1fDA&ixlib=rb-4.1.0&q=85"
            alt="Professional office background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600/75 via-orange-500/75 to-red-600/80"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Corporate Marking</h1>
            <p className="text-xl md:text-2xl text-orange-100">
              Your trusted partner in intellectual property protection and trademark services
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Our Story</h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Corporate Marking was established with a singular mission: to make trademark protection accessible, affordable, and straightforward for businesses of all sizes across the United States. We recognized that many entrepreneurs and business owners found the trademark registration process confusing and intimidating, often leading them to either forgo protection entirely or pay exorbitant fees to traditional law firms.
              </p>
              <p>
                We built our company on the principle that every business deserves robust intellectual property protection, regardless of its size or budget. Our team of experienced trademark attorneys and legal professionals has streamlined the registration process, combining cutting-edge technology with personalized service to deliver exceptional results.
              </p>
              <p>
                Today, we proudly serve thousands of clients nationwide, from solo entrepreneurs launching their first product to established corporations expanding their brand portfolio. Our success is measured not just in the number of trademarks we've registered, but in the peace of mind we provide to business owners who trust us to protect their most valuable asset—their brand identity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-6">
                <Target className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Precision</h3>
              <p className="text-gray-600">
                We approach every trademark application with meticulous attention to detail, ensuring accuracy and compliance at every step.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-6">
                <Heart className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Client-First</h3>
              <p className="text-gray-600">
                Your success is our success. We prioritize your needs and provide personalized service that puts you in control.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-6">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Excellence</h3>
              <p className="text-gray-600">
                We maintain the highest standards of legal expertise and customer service, delivering exceptional results consistently.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-6">
                <TrendingUp className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Innovation</h3>
              <p className="text-gray-600">
                We continuously improve our processes and embrace technology to provide faster, more efficient trademark services.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Our Approach</h2>
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Transparency</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We believe in complete transparency throughout the trademark registration process. From pricing to timelines, you'll always know exactly what to expect. No hidden fees, no surprises—just honest, straightforward service.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Expertise</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Our team consists of licensed trademark attorneys and legal professionals with decades of combined experience in intellectual property law. We stay current with USPTO regulations and best practices to provide you with expert guidance and representation.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Technology-Driven</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We leverage advanced technology to streamline the trademark search and filing process, reducing errors and accelerating approval times. Our proprietary systems ensure your application is processed efficiently while maintaining the highest standards of accuracy.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Personalized Service</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Every client is assigned a dedicated case manager who guides them through the entire process. You'll have direct access to our team for questions, updates, and support whenever you need it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl leading-relaxed text-orange-100">
              To empower businesses and entrepreneurs by providing accessible, affordable, and comprehensive trademark protection services. We are committed to safeguarding the brands that drive American innovation and commerce, ensuring that every business owner can confidently build and protect their brand identity.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section with Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left Side - Image */}
            <div className="w-full">
              <img
                src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2V8ZW58MHx8fHwxNzY0MzYzNjc1fDA&ixlib=rb-4.1.0&q=85"
                alt="Professional office environment"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>

            {/* Right Side - Content */}
            <div className="w-full">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                What Sets Us Apart
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                In a crowded marketplace of trademark service providers, Corporate Marking stands out through our unwavering commitment to client success, transparent communication, and exceptional results.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <Award className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Industry-Leading Expertise</h3>
                    <p className="text-gray-600">Our attorneys have handled thousands of trademark applications across every industry, giving us unique insights into what works and what doesn't.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Personalized Attention</h3>
                    <p className="text-gray-600">Every client receives dedicated support from a case manager who understands your business and guides you through each step of the process.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Efficient Processing</h3>
                    <p className="text-gray-600">We leverage technology and streamlined processes to minimize delays while maintaining the highest standards of quality and accuracy.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* By The Numbers Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Impact in Numbers</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Trusted by thousands of businesses nationwide to protect what matters most
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="text-5xl font-bold text-orange-600 mb-2">10,000+</div>
              <div className="text-gray-900 font-semibold mb-2">Trademarks Filed</div>
              <p className="text-gray-600 text-sm">Successfully registered across all 50 states</p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="text-5xl font-bold text-orange-600 mb-2">98%</div>
              <div className="text-gray-900 font-semibold mb-2">Approval Rate</div>
              <p className="text-gray-600 text-sm">Industry-leading success in trademark applications</p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="text-5xl font-bold text-orange-600 mb-2">15+</div>
              <div className="text-gray-900 font-semibold mb-2">Years Experience</div>
              <p className="text-gray-600 text-sm">Decades of combined legal expertise</p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="text-5xl font-bold text-orange-600 mb-2">24/7</div>
              <div className="text-gray-900 font-semibold mb-2">Client Support</div>
              <p className="text-gray-600 text-sm">Always available when you need us</p>
            </Card>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left Side - Content */}
            <div className="w-full">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                How We Work With You
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                We believe in a collaborative, transparent approach that keeps you informed and empowered throughout the entire trademark registration process.
              </p>
              <div className="space-y-6">
                <div className="border-l-4 border-orange-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Initial Consultation</h3>
                  <p className="text-gray-600">We start with a thorough discussion of your brand, business goals, and trademark needs to develop a customized protection strategy.</p>
                </div>
                <div className="border-l-4 border-orange-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Comprehensive Research</h3>
                  <p className="text-gray-600">Our team conducts extensive searches and analysis to identify potential conflicts and ensure your trademark has the best chance of approval.</p>
                </div>
                <div className="border-l-4 border-orange-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Filing & Management</h3>
                  <p className="text-gray-600">We prepare and file your application with precision, then monitor progress and handle any USPTO communications on your behalf.</p>
                </div>
                <div className="border-l-4 border-orange-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Ongoing Protection</h3>
                  <p className="text-gray-600">After registration, we provide continued monitoring and support to maintain and defend your trademark rights.</p>
                </div>
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="w-full">
              <img
                src="https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2V8ZW58MHx8fHwxNzY0MzYzNjc1fDA&ixlib=rb-4.1.0&q=85"
                alt="Professional workspace"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Our Commitment to You</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transparent Communication</h3>
                <p className="text-gray-600">
                  We keep you informed at every stage with clear, jargon-free updates about your trademark application status and any required actions.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Honest Assessment</h3>
                <p className="text-gray-600">
                  We provide realistic expectations about your trademark's registrability and never promise outcomes we can't deliver.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Proactive Problem-Solving</h3>
                <p className="text-gray-600">
                  When challenges arise, our experienced team quickly develops effective solutions to keep your application moving forward.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Long-Term Partnership</h3>
                <p className="text-gray-600">
                  Your trademark registration is just the beginning. We're here to support your brand protection needs as your business grows.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
